import { z } from 'zod';
import { dateTimeSchema, idSchema, nullableDateTimeSchema } from './common';
import { abortReasonSchema, audioStatusSchema, runStatusSchema } from './enums';
import { audioAssetSchema, takeSchema } from './rows';

/** SPEC §6 — generation runs, worker progress, per-asset regeneration. */

/**
 * SPEC §6 — which assets a run queues.
 *
 * The row-selection member COMPOSES a row filter with a mode instead of replacing it:
 * `{rowIds}` alone means "regenerate exactly these rows" (the explicit
 * "Regenerate selected" button), while `{rowIds, only:'missing'|'failed'|'stale'}`
 * narrows to the assets that button actually targeted. Without the mode, a
 * "Retry failed (1)" click on a row whose sibling language is APPROVED would
 * regenerate that sibling too, silently un-approving a published clip.
 * `only` is optional so the original `{rowIds}` payload keeps its meaning.
 */
export const GENERATION_SCOPE_MODES = ['all', 'missing', 'failed', 'stale'] as const;
export const generationScopeModeSchema = z.enum(GENERATION_SCOPE_MODES);
export type GenerationScopeMode = z.infer<typeof generationScopeModeSchema>;

export const generationRowScopeSchema = z.object({
  rowIds: z.array(idSchema).min(1).max(5000),
  /** Defaults to `'all'` — i.e. an explicit regeneration of every asset in those rows. */
  only: generationScopeModeSchema.optional(),
});
export type GenerationRowScope = z.infer<typeof generationRowScopeSchema>;

/** `{scope: "all" | "missing" | "failed" | "stale" | {rowIds, only?}}` */
export const generationScopeSchema = z.union([
  z.literal('all'),
  z.literal('missing'),
  z.literal('failed'),
  z.literal('stale'),
  generationRowScopeSchema,
]);
export type GenerationScope = z.infer<typeof generationScopeSchema>;

export const runTotalsSchema = z.object({
  queued: z.number().int().min(0),
  done: z.number().int().min(0),
  failed: z.number().int().min(0),
});
export type RunTotals = z.infer<typeof runTotalsSchema>;

export const runSummarySchema = z.object({
  id: idSchema,
  batchId: idSchema,
  status: runStatusSchema,
  scope: generationScopeSchema,
  totals: runTotalsSchema,
  estimatedCredits: z.number().int().min(0),
  abortReason: abortReasonSchema.nullable(),
  startedById: idSchema,
  startedByName: z.string().nullable(),
  startedAt: dateTimeSchema,
  finishedAt: nullableDateTimeSchema,
});
export type RunSummary = z.infer<typeof runSummarySchema>;

/** POST /api/batches/:id/generate */
export const startGenerationRequestSchema = z.object({
  scope: generationScopeSchema,
});
export type StartGenerationRequest = z.infer<typeof startGenerationRequestSchema>;

export const startGenerationResponseSchema = z.object({
  run: runSummarySchema,
  assetsQueued: z.number().int().min(0),
  estimatedCredits: z.number().int().min(0),
  remainingCredits: z.number().int().nullable(),
});
export type StartGenerationResponse = z.infer<typeof startGenerationResponseSchema>;

/** 422 body when the pre-flight credit check fails (SPEC §6). */
export const insufficientCreditsResponseSchema = z.object({
  code: z.literal('insufficient_credits'),
  message: z.string(),
  estimatedCredits: z.number().int().min(0),
  remainingCredits: z.number().int().min(0),
});
export type InsufficientCreditsResponse = z.infer<typeof insufficientCreditsResponseSchema>;

/** 422 body while either voiceId is "" (SPEC §8). */
export const voicesNotConfiguredResponseSchema = z.object({
  code: z.literal('voices_not_configured'),
  message: z.string(),
});
export type VoicesNotConfiguredResponse = z.infer<typeof voicesNotConfiguredResponseSchema>;

/** 409 body when a run is already RUNNING for the batch. */
export const runAlreadyRunningResponseSchema = z.object({
  code: z.literal('run_already_running'),
  message: z.string(),
  runId: idSchema,
});
export type RunAlreadyRunningResponse = z.infer<typeof runAlreadyRunningResponseSchema>;

/** GET /api/batches/:id/generate/estimate — powers the confirm dialog before generating. */
export const generationEstimateRequestSchema = z.object({ scope: generationScopeSchema });
export type GenerationEstimateRequest = z.infer<typeof generationEstimateRequestSchema>;

export const generationEstimateResponseSchema = z.object({
  assetsQueued: z.number().int().min(0),
  characters: z.number().int().min(0),
  estimatedCredits: z.number().int().min(0),
  remainingCredits: z.number().int().nullable(),
  sufficient: z.boolean().nullable(),
  lookupError: z.string().nullable(),
});
export type GenerationEstimateResponse = z.infer<typeof generationEstimateResponseSchema>;

/** GET /api/batches/:id/progress — polled every 2s while RUNNING (SPEC §6). */
export const assetProgressSchema = z.object({
  id: idSchema,
  rowId: idSchema,
  status: audioStatusSchema,
  errorCode: z.string().nullable(),
});
export type AssetProgress = z.infer<typeof assetProgressSchema>;

export const progressResponseSchema = z.object({
  run: runSummarySchema.nullable(),
  counts: z.object({
    PENDING: z.number().int().min(0),
    GENERATING: z.number().int().min(0),
    FAILED: z.number().int().min(0),
    GENERATED: z.number().int().min(0),
    APPROVED: z.number().int().min(0),
    STALE: z.number().int().min(0),
  }),
  assets: z.array(assetProgressSchema),
});
export type ProgressResponse = z.infer<typeof progressResponseSchema>;

/** POST /api/runs/:id/abort */
export const abortRunRequestSchema = z.object({
  reason: abortReasonSchema.default('cancelled_by_user'),
});
export type AbortRunRequest = z.infer<typeof abortRunRequestSchema>;

export const abortRunResponseSchema = z.object({ run: runSummarySchema });
export type AbortRunResponse = z.infer<typeof abortRunResponseSchema>;

/** GET /api/batches/:id/runs */
export const listRunsResponseSchema = z.object({ runs: z.array(runSummarySchema) });
export type ListRunsResponse = z.infer<typeof listRunsResponseSchema>;

/** POST /api/assets/:id/regenerate (SPEC §6). */
export const regenerateAssetResponseSchema = z.object({
  asset: audioAssetSchema,
  newTake: takeSchema,
  previousTake: takeSchema.nullable(),
  /** true after 3 regenerations of one asset — the UI nudges to the spoken override. */
  suggestOverride: z.boolean(),
});
export type RegenerateAssetResponse = z.infer<typeof regenerateAssetResponseSchema>;

/** POST /api/assets/:id/select-take */
export const selectTakeRequestSchema = z.object({ takeId: idSchema });
export type SelectTakeRequest = z.infer<typeof selectTakeRequestSchema>;

export const selectTakeResponseSchema = z.object({ asset: audioAssetSchema });
export type SelectTakeResponse = z.infer<typeof selectTakeResponseSchema>;

/** GET /api/assets/:id/takes */
export const listTakesResponseSchema = z.object({ takes: z.array(takeSchema) });
export type ListTakesResponse = z.infer<typeof listTakesResponseSchema>;

/** GET /api/assets/:id */
export const assetResponseSchema = z.object({ asset: audioAssetSchema });
export type AssetResponse = z.infer<typeof assetResponseSchema>;
